/**
 * Regeneration Lock - Phase 4A
 *
 * Provides schema-wide mutual exclusion during scoped regeneration.
 * Enforces that only one regeneration session can mutate the canonical
 * schema at a time, and that all other mutation authorities are blocked
 * for the duration of the session.
 *
 * Governance invariants:
 *   - Only one active lock per scope at any time (Phase 4A: schema scope only)
 *   - Expired lock → fail-open (treated as unlocked without requiring explicit release)
 *   - Abandonment (HMR, reload, crash) is distinct from completion/rollback
 *   - Lock IDs are for tracking only - deterministic counter-based, not canonical IDs
 *   - No AI calls or external I/O during active lock acquisition/release
 *   - Pre-acquisition schema is stored in-memory for rollback; never persisted here
 */

import type { NuvraSchema } from "@/core/schema/nuvraSchema";
import { eventBus } from "@/runtime/eventBus";
import { logger } from "@/system/logger";

// ─── Types ───

export type LockScope = "schema"; // Phase 4A: schema-wide only; sub-scopes deferred to Phase 4B

export type LockReleaseReason =
  | "completed"
  | "rolled_back"
  | "expired"
  | "forced"
  | "persistence_restore";

export type LockAbandonReason =
  | "runtime_reload"
  | "persistence_restore"
  | "hmr"
  | "crash_recovery"
  | "process_restart";

export interface RegenerationLockToken {
  readonly id:                   string;
  readonly scope:                LockScope;
  readonly holder:               string;
  readonly acquiredAt:           number;
  readonly expiresAt:            number;
  readonly fingerprintBefore:    string;
  readonly preAcquisitionSchema: NuvraSchema | null;
}

export interface LockMetrics {
  acquisitions:        number;
  releases:            number;
  expirations:         number;
  rollbacks:           number;
  abandonments:        number;
  failOpenActivations: number;
  avgDurationMs:       number | null;
}

// ─── Config ───

const MAX_TTL_MS = 300_000; // 5 minutes - absolute ceiling on any lock TTL

// ─── State ───

let _activeLock:    RegenerationLockToken | null = null;
let _expiryTimer:   ReturnType<typeof setTimeout> | null = null;

let _acquisitions        = 0;
let _releases            = 0;
let _expirations         = 0;
let _rollbacks           = 0;
let _abandonments        = 0;
let _failOpenActivations = 0;
let _totalDurationMs     = 0;
let _completedCount      = 0;
let _lockIdCounter       = 0;

// ─── Internal helpers ───

function _generateLockId(): string {
  _lockIdCounter++;
  return `regen_${Date.now().toString(36)}_${_lockIdCounter}`;
}

function _clearExpiryTimer(): void {
  if (_expiryTimer !== null) {
    clearTimeout(_expiryTimer);
    _expiryTimer = null;
  }
}

function _computeDurationMs(token: RegenerationLockToken): number {
  return Date.now() - token.acquiredAt;
}

function _getProjectId(token: RegenerationLockToken): string {
  return token.preAcquisitionSchema?.projectId ?? "";
}

// ─── Expiry timer callback ───

function _onLockExpired(): void {
  if (!_activeLock) return;

  const token = _activeLock;
  const durationMs = _computeDurationMs(token);
  const projectId  = _getProjectId(token);

  _expirations++;
  _releases++;
  _activeLock  = null;
  _expiryTimer = null;

  logger.warn("[RegenerationLock] Lock expired by TTL - fail-open; lock cleared", {
    holder: token.holder,
    durationMs,
  });

  eventBus.emit({
    type:      "regeneration:lock_released",
    projectId,
    scope:     token.scope,
    holder:    token.holder,
    reason:    "expired",
    durationMs,
  });
}

// ─── Public API ───

/**
 * Attempt to acquire a schema-wide regeneration lock.
 *
 * If an unexpired lock exists, returns null (caller must not proceed).
 * If the existing lock is expired, clears it (fail-open) before acquiring.
 * TTL is clamped to MAX_TTL_MS regardless of the requested value.
 */
export function acquireRegenerationLock(
  holder:               string,
  ttlMs:                number,
  fingerprintBefore:    string,
  preAcquisitionSchema: NuvraSchema | null,
): RegenerationLockToken | null {
  // Fail-open: if there is an expired lock, clear it silently before attempting acquisition
  if (_activeLock && Date.now() > _activeLock.expiresAt) {
    logger.warn("[RegenerationLock] Stale expired lock found on acquire - clearing (fail-open)", {
      holder:       _activeLock.holder,
      expiredAgoMs: Date.now() - _activeLock.expiresAt,
    });
    _clearExpiryTimer();
    _expirations++;
    _releases++;
    const stale = _activeLock;
    _activeLock = null;
    eventBus.emit({
      type:      "regeneration:lock_released",
      projectId: _getProjectId(stale),
      scope:     stale.scope,
      holder:    stale.holder,
      reason:    "expired",
      durationMs: _computeDurationMs(stale),
    });
  }

  if (_activeLock) {
    logger.warn("[RegenerationLock] Acquisition refused - lock already held", {
      requestedBy:  holder,
      currentHolder: _activeLock.holder,
    });
    return null;
  }

  const clampedTtl  = Math.min(Math.max(ttlMs, 1), MAX_TTL_MS);
  const now         = Date.now();
  const projectId   = preAcquisitionSchema?.projectId ?? "";

  const token: RegenerationLockToken = {
    id:                   _generateLockId(),
    scope:                "schema",
    holder,
    acquiredAt:           now,
    expiresAt:            now + clampedTtl,
    fingerprintBefore,
    preAcquisitionSchema,
  };

  _activeLock   = token;
  _acquisitions++;

  _clearExpiryTimer();
  _expiryTimer = setTimeout(_onLockExpired, clampedTtl);

  logger.info("[RegenerationLock] Lock acquired", {
    id:     token.id,
    holder,
    ttlMs:  clampedTtl,
    projectId,
  });

  eventBus.emit({
    type:             "regeneration:lock_acquired",
    projectId,
    scope:            "schema",
    holder,
    ttlMs:            clampedTtl,
    fingerprintBefore,
  });

  return token;
}

/**
 * Extend an active lock's TTL.
 * Returns true if extended, false if the token doesn't match the active lock.
 * The resulting expiresAt is clamped to acquiredAt + MAX_TTL_MS.
 */
export function extendRegenerationLock(
  token:        RegenerationLockToken,
  additionalMs: number,
): boolean {
  if (!_activeLock || _activeLock.id !== token.id) {
    logger.warn("[RegenerationLock] extendRegenerationLock: token mismatch or no active lock");
    return false;
  }

  const newExpiresAt = Math.min(
    _activeLock.expiresAt + additionalMs,
    _activeLock.acquiredAt + MAX_TTL_MS,
  );

  // Rebuild token with new expiresAt (tokens are readonly - replace reference)
  const extended: RegenerationLockToken = {
    ..._activeLock,
    expiresAt: newExpiresAt,
  };
  _activeLock = extended;

  _clearExpiryTimer();
  const remaining = newExpiresAt - Date.now();
  if (remaining > 0) {
    _expiryTimer = setTimeout(_onLockExpired, remaining);
  }

  logger.debug("[RegenerationLock] Lock extended", {
    id:           token.id,
    newExpiresAt,
    remainingMs:  remaining,
  });

  return true;
}

/**
 * Release a lock on normal completion or rollback.
 *
 * "completed": records duration for avgDurationMs, emits regeneration:completed
 * "rolled_back": restores pre-acquisition schema via canonical undo path,
 *                emits regeneration:rollback, then regeneration:lock_released
 */
export function releaseRegenerationLock(
  token: RegenerationLockToken,
  reason: "completed" | "rolled_back",
  opts?: { fingerprintAfter?: string; mutationsApplied?: number },
): void {
  if (!_activeLock || _activeLock.id !== token.id) {
    logger.warn("[RegenerationLock] releaseRegenerationLock: token mismatch or no active lock", {
      reason,
      tokenId: token.id,
    });
    return;
  }

  _clearExpiryTimer();
  const durationMs = _computeDurationMs(token);
  const projectId  = _getProjectId(token);

  _activeLock = null;
  _releases++;

  if (reason === "rolled_back") {
    _rollbacks++;
    const schema = token.preAcquisitionSchema;

    if (schema) {
      // Restore canonical schema via the undo event path reused from Phase 3B/3C
      eventBus.emit({
        type:                 "schema:undo_canonical_available",
        projectId,
        schema,
        isRegenerationRollback: true,
      });
      eventBus.emit({
        type:      "schema:staleness_detected",
        reason:    "undo",
        projectId,
      });
    } else {
      logger.warn("[RegenerationLock] Rollback requested but no pre-acquisition schema stored", {
        holder: token.holder,
      });
    }

    eventBus.emit({
      type:              "regeneration:rollback",
      projectId,
      scope:             token.scope,
      holder:            token.holder,
      reason:            "failure",
      fingerprintBefore: token.fingerprintBefore,
      canonicalRestored: schema !== null,
    });

  } else {
    // completed
    _completedCount++;
    _totalDurationMs += durationMs;

    eventBus.emit({
      type:              "regeneration:completed",
      projectId,
      scope:             token.scope,
      holder:            token.holder,
      fingerprintBefore: token.fingerprintBefore,
      fingerprintAfter:  opts?.fingerprintAfter ?? "",
      durationMs,
      mutationsApplied:  opts?.mutationsApplied ?? 0,
    });
  }

  logger.info("[RegenerationLock] Lock released", {
    holder: token.holder,
    reason,
    durationMs,
  });

  eventBus.emit({
    type:      "regeneration:lock_released",
    projectId,
    scope:     token.scope,
    holder:    token.holder,
    reason,
    durationMs,
  });
}

/**
 * Force-release the active lock regardless of token identity.
 * Used by persistence restore and admin override.
 * Does NOT perform schema rollback - caller must handle state reconciliation.
 */
export function forceReleaseRegenerationLock(
  reason: "persistence_restore" | "crash_recovery" | "admin",
): void {
  if (!_activeLock) return;

  _clearExpiryTimer();
  const token      = _activeLock;
  const durationMs = _computeDurationMs(token);
  const projectId  = _getProjectId(token);

  _activeLock = null;
  _releases++;

  const releaseReason = reason === "admin" ? "forced" : reason;

  logger.warn("[RegenerationLock] Lock force-released", {
    holder: token.holder,
    reason,
    durationMs,
  });

  eventBus.emit({
    type:      "regeneration:lock_released",
    projectId,
    scope:     token.scope,
    holder:    token.holder,
    reason:    releaseReason as "forced" | "persistence_restore",
    durationMs,
  });
}

/**
 * Abandon the active lock due to ownership discontinuity.
 * Distinct from release: abandonment means the holder is no longer able to
 * complete or explicitly roll back. The lock is cleared and rollback is
 * attempted if a pre-acquisition schema is available.
 *
 * Abandonment reasons: runtime_reload, persistence_restore, hmr,
 *                      crash_recovery, process_restart
 */
export function abandonRegenerationLock(reason: LockAbandonReason): void {
  if (!_activeLock) return;

  _clearExpiryTimer();
  const token      = _activeLock;
  const durationMs = _computeDurationMs(token);
  const projectId  = _getProjectId(token);

  _activeLock = null;
  _abandonments++;
  _rollbacks++;

  // Attempt canonical rollback via the undo path
  const schema = token.preAcquisitionSchema;
  if (schema) {
    eventBus.emit({
      type:                   "schema:undo_canonical_available",
      projectId,
      schema,
      isRegenerationRollback: true,
    });
    eventBus.emit({
      type:      "schema:staleness_detected",
      reason:    "undo",
      projectId,
    });
  } else {
    logger.warn("[RegenerationLock] Abandonment - no pre-acquisition schema to restore", {
      holder: token.holder,
      reason,
    });
  }

  eventBus.emit({
    type:              "regeneration:rollback",
    projectId,
    scope:             token.scope,
    holder:            token.holder,
    reason:            "failure",
    fingerprintBefore: token.fingerprintBefore,
    canonicalRestored: schema !== null,
  });

  logger.warn("[RegenerationLock] Lock abandoned", {
    holder: token.holder,
    reason,
    durationMs,
  });

  eventBus.emit({
    type:      "regeneration:lock_abandoned",
    projectId,
    scope:     token.scope,
    holder:    token.holder,
    reason,
    durationMs,
  });
}

/**
 * Returns true when a non-expired regeneration lock is active.
 *
 * Fail-open: if the active lock is past its expiresAt but the timer hasn't
 * fired yet, this returns false and increments failOpenActivations.
 * The lock is NOT cleared here - _onLockExpired will clear it when the timer fires.
 */
export function isRegenerationLocked(): boolean {
  if (!_activeLock) return false;

  if (Date.now() > _activeLock.expiresAt) {
    _failOpenActivations++;
    logger.debug("[RegenerationLock] isRegenerationLocked: lock expired - fail-open (treating as unlocked)", {
      holder:     _activeLock.holder,
      expiredMs:  Date.now() - _activeLock.expiresAt,
    });
    return false;
  }

  return true;
}

/**
 * Returns true if the given lockTokenId currently identifies the active lock
 * and that lock has not expired.
 *
 * Used by Tier A session holders to verify they still hold authority
 * immediately before executing their final store write block. This guards
 * against the lock-loss continuation hazard: a Tier C force-release can
 * occur during a long computation phase; without this check, the session
 * would write to the store without valid authority.
 *
 * Side-effect free: never mutates lock state, never logs, never emits events.
 */
export function isLockHeldBy(lockTokenId: string): boolean {
  if (!_activeLock) return false;
  if (Date.now() > _activeLock.expiresAt) return false;
  return _activeLock.id === lockTokenId;
}

/** Return the active lock token, or null if none is held. */
export function getActiveLock(): RegenerationLockToken | null {
  return _activeLock;
}

/** Return live lock observability metrics. */
export function getLockMetrics(): LockMetrics {
  return {
    acquisitions:        _acquisitions,
    releases:            _releases,
    expirations:         _expirations,
    rollbacks:           _rollbacks,
    abandonments:        _abandonments,
    failOpenActivations: _failOpenActivations,
    avgDurationMs:       _completedCount > 0 ? _totalDurationMs / _completedCount : null,
  };
}

/** Reset all lock state. Test use only. */
export function _resetLockForTest(): void {
  _clearExpiryTimer();
  _activeLock          = null;
  _acquisitions        = 0;
  _releases            = 0;
  _expirations         = 0;
  _rollbacks           = 0;
  _abandonments        = 0;
  _failOpenActivations = 0;
  _totalDurationMs     = 0;
  _completedCount      = 0;
  _lockIdCounter       = 0;
}
